import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, useSearchParams, Navigate } from 'react-router-dom';
import SignUpForm from '../../components/auth/SignUpForm';
import AuthLayout from '../../components/auth/AuthLayout';
import CheckoutLoadingScreen from '../../components/auth/CheckoutLoadingScreen';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import { useToast } from '../../context/ToastContext';
import { handlePostAuthRedirect, stashPostAuthRedirect, clearPostAuthRedirect, isSafeAppRedirect } from '../../utils/authRedirect';
import { clearCheckoutState, hasPendingCheckout, isReturningFromStripe } from '../../utils/checkoutStorage';
import { getPasswordRecoveryEmail, isPasswordRecoveryPending } from '../../utils/passwordRecoveryStorage';
import { clearLoggingOutFlag } from '../../utils/authReset';

const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const { showToast } = useToast();
  const checkoutHandledRef = useRef(false);
  const [shouldRedirectToPricing] = useState(false);

  // Stuck logout guard blocks auth events → signup → /app → bounce loop.
  useEffect(() => {
    clearLoggingOutFlag();
  }, []);

  // Standard SaaS: If user comes back from Stripe via browser back,
  // normalize to pricing cancellation flow so the toast is shown consistently.
  useEffect(() => {
    if (isReturningFromStripe()) {
      clearCheckoutState();
      window.location.replace('/pricing?checkout=cancelled');
    }
  }, []);

  // Keep deep link across Google OAuth (callback always lands on /app).
  useEffect(() => {
    const redirect = searchParams.get('redirect');
    if (isSafeAppRedirect(redirect)) {
      stashPostAuthRedirect(redirect);
    } else {
      clearPostAuthRedirect();
    }
  }, [searchParams]);

  // Handle all post-authentication redirect scenarios
  useEffect(() => {
    void handlePostAuthRedirect(user, loading, searchParams, location, checkoutHandledRef, showToast, navigate, t);
  }, [user, loading, location, searchParams, showToast, navigate, t]);

  // If redirecting to pricing (cancelled checkout), don't render anything
  if (shouldRedirectToPricing) {
    return null;
  }

  // Note: we intentionally do NOT show a fullscreen takeover for checkout here.
  // Standard SaaS UX: keep the primary button in loading state (handled by SignUpForm),
  // and disable inputs while we verify subscription / start checkout.

  // Show loading screen when user is authenticated (prevents flash before redirect)
  if (!loading && user) {
    const recoveryEmail = getPasswordRecoveryEmail();
    if (isPasswordRecoveryPending() && recoveryEmail) {
      return (
        <>
          <CheckoutLoadingScreen messageKey="auth.redirectingToResetPassword" />
          <Navigate to={`/reset-password?email=${encodeURIComponent(recoveryEmail)}`} replace />
        </>
      );
    }

    const hasPending = hasPendingCheckout();
    
    // Standard SaaS: when a checkout intent exists, do NOT redirect to /app.
    // We are going to Stripe checkout next.
    if (hasPending) {
      return <CheckoutLoadingScreen messageKey="auth.redirectingToPayment" />;
    }
    
    // Show loading for standard redirect to app
    {
      const redirectParam = searchParams.get('redirect');
      const redirectTo = isSafeAppRedirect(redirectParam) ? redirectParam : '/app';
      
      // Safety: avoid infinite loops
      if (!redirectTo.startsWith('/login') && !redirectTo.startsWith('/signup')) {
        return (
          <>
            <CheckoutLoadingScreen messageKey="auth.redirectingToApp" />
            <Navigate to={redirectTo} replace />
          </>
        );
      }
      return (
        <>
          <CheckoutLoadingScreen messageKey="auth.redirectingToApp" />
          <Navigate to="/app" replace />
        </>
      );
    }
  }

  return (
    <AuthLayout>
      <SignUpForm 
        onSwitchToLogin={() => {
          const redirect = searchParams.get('redirect');
          const to = redirect ? `/login?redirect=${encodeURIComponent(redirect)}` : '/login';
          navigate(to, { replace: true });
        }}
      />
    </AuthLayout>
  );
};

export default SignUpPage;
